import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Autocomplete,
    TextField,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import BoltIcon from '@mui/icons-material/Bolt';

const PANEL_ANIMATION = `
@keyframes macroButtonFadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
}
`;

const ButtonMacro = ({
    macroList,
    macroSelected,
    macroSelectedActions,
    loadingRequestMacroList,
    loadingMacroAccordion,
    isApplyingMacro,
    onSelectMacro,
    onApply,
    onSave,
    onDismiss,
}) => {
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [buttonPos, setButtonPos] = useState(null);
    const anchorRef = useRef(null);

    const hasActions = macroSelectedActions && macroSelectedActions.length > 0;
    const isLoadingActions = loadingMacroAccordion && loadingMacroAccordion !== false;

    const handleToggle = () => setOpen(prev => !prev);

    useEffect(() => {
        const updatePosition = () => {
            if (anchorRef.current) {
                const rect = anchorRef.current.getBoundingClientRect();
                setButtonPos({ top: rect.top, left: rect.left });
            }
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        return () => window.removeEventListener('resize', updatePosition);
    }, []);

    const pos = buttonPos ?? { top: 170, left: typeof window !== 'undefined' ? window.innerWidth - 200 : 0 };

    const floatingUI = (
        <>
            <style>{PANEL_ANIMATION}</style>
            <div style={{
                position: 'fixed',
                top: pos.top - 18,
                left: '65vw',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            }}>
                {/* Toggle button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Tooltip title={open ? 'Fechar macros' : 'Automatizar com macro'} placement="right">
                        <button
                            onClick={handleToggle}
                            onMouseEnter={() => setHovered(true)}
                            onMouseLeave={() => setHovered(false)}
                            style={{
                                background: open
                                    ? 'var(--mexx-green-dark, #3f51b4)'
                                    : 'var(--mexx-green-primary, #2979ff)',
                                color: '#fff',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: 999,
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                transition: 'all .2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <BoltIcon style={{ fontSize: 16, color: '#fff' }} />
                            Automatizar
                        </button>
                    </Tooltip>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDismiss?.(); }}
                        title="Fechar"
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'rgba(80,80,80,0.5)',
                            cursor: 'pointer',
                            padding: '2px 3px',
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: 1,
                        }}
                    >
                        <CloseIcon style={{ fontSize: 13 }} />
                    </button>
                </div>

                {/* Panel */}
                {open && (
                    <div style={{
                        width: 320,
                        marginTop: 8,
                        background: 'var(--mexx-bg-elevated, #fff)',
                        borderRadius: 12,
                        boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
                        padding: '16px',
                        border: '1px solid var(--mexx-gray-200, #EEEEEE)',
                        animation: 'macroButtonFadeUp .2s ease',
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 14,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <BoltIcon style={{ fontSize: 18, color: 'var(--mexx-green-primary, #2979ff)' }} />
                                <Typography style={{ fontWeight: 600, fontSize: 14, color: 'var(--mexx-gray-800, #424242)' }}>
                                    Automatizar com macro
                                </Typography>
                            </div>
                            <IconButton size="small" onClick={handleToggle} style={{ color: 'var(--mexx-gray-500, #9E9E9E)' }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>

                        {/* Macro select */}
                        <Autocomplete
                            size="small"
                            options={macroList || []}
                            getOptionLabel={opt => opt.name || ''}
                            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                            value={macroSelected || null}
                            loading={loadingRequestMacroList}
                            onChange={(_, newVal) => onSelectMacro(newVal)}
                            disablePortal
                            componentsProps={{
                                popper: {
                                    style: { zIndex: 10 },
                                },
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Selecionar macro"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingRequestMacroList
                                                    ? <CircularProgress size={14} color="inherit" />
                                                    : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'var(--mexx-green-primary, #2979ff)',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: 'var(--mexx-green-primary, #2979ff)',
                                        },
                                    }}
                                />
                            )}
                        />

                        {/* Actions preview */}
                        {isLoadingActions && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                                <CircularProgress size={20} style={{ color: 'var(--mexx-green-primary, #2979ff)' }} />
                            </div>
                        )}

                        {!isLoadingActions && hasActions && (
                            <div style={{
                                marginTop: 12,
                                background: 'var(--mexx-green-subtle, #e9edf8)',
                                borderRadius: 8,
                                padding: '8px 10px',
                            }}>
                                {macroSelectedActions.map(action => (
                                    <div key={action.id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        marginBottom: 4,
                                    }}>
                                        <CheckCircleOutlineIcon style={{
                                            fontSize: 14,
                                            color: 'var(--mexx-green-primary, #2979ff)',
                                            flexShrink: 0,
                                        }} />
                                        <Typography style={{ fontSize: 12, color: 'var(--mexx-gray-700, #616161)' }}>
                                            {action.automationactionname}
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Buttons */}
                        {hasActions && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                {isApplyingMacro != null && onSave ? (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="small"
                                        startIcon={<SaveIcon />}
                                        onClick={onSave}
                                        sx={{
                                            background: 'var(--mexx-green-primary, #2979ff)',
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            '&:hover': {
                                                background: 'var(--mexx-green-dark, #3f51b4)',
                                            },
                                        }}
                                    >
                                        Salvar chamado
                                    </Button>
                                ) : (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="small"
                                        startIcon={isApplyingMacro != null
                                            ? <CheckCircleOutlineIcon style={{ fontSize: 16, color: '#fff' }} />
                                            : <BoltIcon style={{ fontSize: 16, color: '#fff' }} />
                                        }
                                        disabled={isApplyingMacro != null}
                                        onClick={onApply}
                                        sx={{
                                            background: 'var(--mexx-green-primary, #2979ff)',
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            '&:hover': {
                                                background: 'var(--mexx-green-dark, #3f51b4)',
                                            },
                                            '&.Mui-disabled': {
                                                background: 'var(--mexx-green-primary, #2979ff)',
                                                opacity: 0.7,
                                                color: '#fff',
                                            },
                                        }}
                                    >
                                        {isApplyingMacro != null ? 'Macro aplicada' : 'Aplicar macro'}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );

    return (
        <>
            {/* Invisible anchor — marks the target position next to the title */}
            <span ref={anchorRef} style={{ display: 'inline-block' }} />
            {createPortal(floatingUI, document.body)}
        </>
    );
};

export default ButtonMacro;
